import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Edit3, Save, X, Camera, MapPin, Briefcase, Globe,
  Linkedin, UserPlus, Check, Clock, Users, Award, Loader2, Upload
} from 'lucide-react';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [editData, setEditData] = useState({});
  const [uploading, setUploading] = useState(false);

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');
  const isOwnProfile = user.user_id === id;

  useEffect(() => {
    loadProfile();
    if (!isOwnProfile) loadConnectionStatus();
  }, [id]);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/members/profile/${id}`);
      setProfile(response.data);
      setEditData({
        full_name: response.data.full_name || '',
        headline: response.data.headline || '',
        bio: response.data.bio || '',
        location: response.data.location || '',
        industry: response.data.industry || '',
        website: response.data.website || '',
        linkedin: response.data.linkedin || '',
        skills_list: response.data.skills_list || '',
        photo_url: response.data.photo_url || ''
      });
    } catch (error) {
      console.error('Failed to load profile', error);
    } finally { setLoading(false); }
  };

  const loadConnectionStatus = async () => {
    try {
      const res = await axios.get(`${API}/connections/status/${user.user_id}/${id}`);
      setConnectionStatus(res.data);
    } catch (e) { /* no connection */ }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/members/profile/${id}`, editData);
      toast.success('Profile updated!');
      setEditing(false);
      loadProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleConnect = async () => {
    try {
      const res = await axios.post(`${API}/connections/request`, {
        from_user_id: user.user_id,
        to_user_id: id
      });
      if (res.data.status === 'already_exists') {
        toast.info('Connection already exists');
      } else {
        toast.success('Connection request sent!');
      }
      loadConnectionStatus();
    } catch (error) {
      toast.error('Failed to send connection request');
    }
  };

  const handleAccept = async (connectionId) => {
    try {
      await axios.post(`${API}/connections/respond`, {
        connection_id: connectionId,
        action: 'accept'
      });
      toast.success('Connection accepted!');
      loadConnectionStatus();
    } catch (error) {
      toast.error('Failed to accept connection');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const photoUrl = `${BACKEND_URL}${res.data.url}`;
      setEditData(prev => ({ ...prev, photo_url: photoUrl }));
      toast.success('Photo uploaded!');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <AppShell><div className="flex items-center justify-center py-20"><div className="text-[#A39BB0] font-inter">Loading profile...</div></div></AppShell>;
  }

  if (!profile) {
    return <AppShell><div className="flex items-center justify-center py-20"><div className="text-[#A39BB0] font-inter">Profile not found</div></div></AppShell>;
  }

  const photoUrl = editing ? editData.photo_url : profile.photo_url;
  const initial = profile.full_name?.charAt(0)?.toUpperCase() || '?';

  return (
    <AppShell>
      {/* Profile Banner */}
      <div className="bg-gradient-to-r from-purple-600/30 via-pink-500/20 to-purple-600/30 h-32 relative" />

      <div className="max-w-4xl mx-auto px-4 md:px-8 -mt-16 relative z-10">
        <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-8 mb-6" data-testid="profile-card">
          <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
            {/* Avatar */}
            <div className="relative">
              {photoUrl ? (
                <img src={photoUrl} alt={profile.full_name} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg" />
              ) : (
                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <span className="text-white font-cormorant font-bold text-3xl">{initial}</span>
                </div>
              )}
              {editing && (
                <>
                  <input type="file" id="photo-upload" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  <label htmlFor="photo-upload" className="absolute bottom-0 right-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white shadow-md hover:bg-purple-700 transition-colors cursor-pointer" data-testid="change-photo-button">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  </label>
                </>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              {editing ? (
                <div className="space-y-3">
                  <input value={editData.full_name} onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                    className="text-2xl font-cormorant font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1 w-full focus:border-purple-400 outline-none"
                    placeholder="Your name" data-testid="edit-name-input" />
                  <input value={editData.headline} onChange={(e) => setEditData({...editData, headline: e.target.value})}
                    className="text-gray-600 font-inter text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-1 w-full focus:border-purple-400 outline-none"
                    placeholder="Your headline (e.g., Career Strategist | AI Enthusiast)" data-testid="edit-headline-input" />
                </div>
              ) : (
                <>
                  <h1 className="text-2xl sm:text-3xl font-cormorant font-bold text-white" data-testid="profile-name">{profile.full_name}</h1>
                  {profile.headline && <p className="text-[#A39BB0] font-inter text-sm mt-1">{profile.headline}</p>}
                </>
              )}

              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {profile.location && !editing && (
                  <span className="flex items-center space-x-1 text-[#A39BB0] text-xs font-inter">
                    <MapPin className="w-3.5 h-3.5" /><span>{profile.location}</span>
                  </span>
                )}
                {profile.industry && !editing && (
                  <span className="flex items-center space-x-1 text-[#A39BB0] text-xs font-inter">
                    <Briefcase className="w-3.5 h-3.5" /><span>{profile.industry}</span>
                  </span>
                )}
                {profile.archetype && (
                  <span className="bg-purple-500/20 text-purple-300 border border-purple-500/20 px-3 py-1 rounded-full text-xs font-inter font-medium flex items-center space-x-1">
                    <Award className="w-3 h-3" /><span>{profile.archetype}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex-shrink-0">
              {isOwnProfile ? (
                editing ? (
                  <div className="flex space-x-2">
                    <button onClick={handleSave} className="flex items-center space-x-1 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-medium text-sm px-4 py-2 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all" data-testid="save-profile-button">
                      <Save className="w-4 h-4" /><span>Save</span>
                    </button>
                    <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600 p-2" data-testid="cancel-edit-button">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setEditing(true)} className="flex items-center space-x-1 bg-white/10 hover:bg-white/15 text-white border border-white/10 font-inter font-medium text-sm px-4 py-2 rounded-full transition-all" data-testid="edit-profile-button">
                    <Edit3 className="w-4 h-4" /><span>Edit Profile</span>
                  </button>
                )
              ) : (
                connectionStatus?.status === 'connected' ? (
                  <span className="flex items-center space-x-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/20 font-inter font-medium text-sm px-4 py-2 rounded-full">
                    <Check className="w-4 h-4" /><span>Connected</span>
                  </span>
                ) : connectionStatus?.status === 'pending' && connectionStatus?.from_user_id !== user.user_id ? (
                  <button onClick={() => handleAccept(connectionStatus.connection_id)} className="flex items-center space-x-1 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-medium text-sm px-4 py-2 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all" data-testid="accept-connection-button">
                    <Check className="w-4 h-4" /><span>Accept</span>
                  </button>
                ) : connectionStatus?.status === 'pending' ? (
                  <span className="flex items-center space-x-1 bg-amber-500/20 text-amber-300 border border-amber-500/20 font-inter font-medium text-sm px-4 py-2 rounded-full">
                    <Clock className="w-4 h-4" /><span>Pending</span>
                  </span>
                ) : (
                  <button onClick={handleConnect} className="flex items-center space-x-1 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-medium text-sm px-4 py-2 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all" data-testid="connect-button">
                    <UserPlus className="w-4 h-4" /><span>Connect</span>
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        {/* Bio & Details */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6">
              <h2 className="text-lg font-cormorant font-bold text-white mb-3">About</h2>
              {editing ? (
                <textarea value={editData.bio} onChange={(e) => setEditData({...editData, bio: e.target.value})}
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-3 text-white focus:border-purple-500/50 outline-none min-h-[120px] font-inter text-sm resize-none placeholder:text-[#A39BB0]/40"
                  placeholder="Tell the network about yourself..." data-testid="edit-bio-input" />
              ) : (
                <p className="text-white/70 font-inter text-sm leading-relaxed whitespace-pre-wrap">
                  {profile.bio || 'No bio yet. Edit your profile to share your story.'}
                </p>
              )}
            </div>
            <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6">
              <h2 className="text-lg font-cormorant font-bold text-white mb-3">Skills & Expertise</h2>
              {editing ? (
                <input value={editData.skills_list} onChange={(e) => setEditData({...editData, skills_list: e.target.value})}
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-3 text-white focus:border-purple-500/50 outline-none font-inter text-sm placeholder:text-[#A39BB0]/40"
                  placeholder="Comma-separated: Leadership, AI Strategy..." data-testid="edit-skills-input" />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(profile.skills_list || '').split(',').filter(s => s.trim()).map((skill, i) => (
                    <span key={i} className="bg-purple-500/20 text-purple-300 border border-purple-500/20 px-3 py-1 rounded-full text-xs font-inter font-medium">{skill.trim()}</span>
                  ))}
                  {!profile.skills_list && <p className="text-[#A39BB0] font-inter text-sm">No skills added yet</p>}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-6">
            <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6">
              <h2 className="text-lg font-cormorant font-bold text-white mb-4">Details</h2>
              {editing ? (
                <div className="space-y-3">
                  {[
                    { key: 'location', label: 'Location', placeholder: 'City, State', testId: 'edit-location-input' },
                    { key: 'industry', label: 'Industry', placeholder: 'Tech, Finance...', testId: 'edit-industry-input' },
                    { key: 'website', label: 'Website', placeholder: 'https://', testId: 'edit-website-input' },
                    { key: 'linkedin', label: 'LinkedIn', placeholder: 'linkedin.com/in/...', testId: 'edit-linkedin-input' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-[#A39BB0] text-xs font-inter">{f.label}</label>
                      <input value={editData[f.key] || ''} onChange={(e) => setEditData({...editData, [f.key]: e.target.value})}
                        className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-purple-500/50 outline-none placeholder:text-[#A39BB0]/40"
                        placeholder={f.placeholder} data-testid={f.testId} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {profile.location && (
                    <div className="flex items-center space-x-2 text-white/70 text-sm font-inter"><MapPin className="w-4 h-4 text-[#A39BB0]" /><span>{profile.location}</span></div>
                  )}
                  {profile.industry && (
                    <div className="flex items-center space-x-2 text-white/70 text-sm font-inter"><Briefcase className="w-4 h-4 text-[#A39BB0]" /><span>{profile.industry}</span></div>
                  )}
                  {profile.website && (
                    <a href={profile.website} target="_blank" rel="noreferrer" className="flex items-center space-x-2 text-purple-300 text-sm font-inter hover:text-purple-200"><Globe className="w-4 h-4" /><span>{profile.website}</span></a>
                  )}
                  {profile.linkedin && (
                    <a href={profile.linkedin} target="_blank" rel="noreferrer" className="flex items-center space-x-2 text-purple-300 text-sm font-inter hover:text-purple-200"><Linkedin className="w-4 h-4" /><span>LinkedIn</span></a>
                  )}
                  <div className="pt-3 border-t border-white/[0.06]">
                    <div className="text-[#A39BB0] text-xs font-inter">Member since</div>
                    <div className="text-white/70 text-sm font-inter">{new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
